import { HexColorSchema } from "#schemas/primitives.ts";
import { domainRegistry } from "#schemas/registry.ts";
import { z } from "zod";

/** Tool input: request a palette from a theme prompt. */
export const PaletteRequestSchema = z.object({
  theme: z.string().min(1).max(200),
  size: z.int().min(2).max(64).optional(),
}).register(domainRegistry, {
  id: "palette_request",
  title: "Palette request",
  description: "Theme prompt and optional palette size for AI palette generation.",
  surface: "ai",
  llmExposed: true,
  group: "tools",
  examples: [{ theme: "sunset desert", size: 16 }],
});
export type PaletteRequest = z.infer<typeof PaletteRequestSchema>;

/** Bare palette: array of hex colors emitted by the model. */
export const PaletteSchema = z.array(HexColorSchema).min(1).register(domainRegistry, {
  id: "palette",
  title: "Palette",
  description: "Ordered array of #RRGGBB hex colors (at least one).",
  surface: "ai",
  llmExposed: true,
  group: "tools",
  examples: [["#FF0000", "#00FF00", "#0000FF"]],
});
export type Palette = z.infer<typeof PaletteSchema>;

/** Wire response from /api/ai/palette. */
export const PaletteResponseSchema = z.object({
  theme: z.string(),
  palette: PaletteSchema,
}).register(domainRegistry, {
  id: "palette_response",
  title: "Palette response",
  description: "Echo of theme plus generated palette.",
  surface: "ai",
  llmExposed: false,
  group: "tools",
});
export type PaletteResponse = z.infer<typeof PaletteResponseSchema>;

/**
 * Tool input: write an entire frame (one layer) in a single call using
 * palette-indexed RLE. The RLE is space-separated `idx:count` tokens whose
 * counts sum to `rows * cols` (1024 for the standard 32x32 canvas).
 *
 * Index 0 always means "transparent" (empty cell). Indices >= 1 select a
 * color: `paletteOverride[i - 1]` if `paletteOverride` is supplied, otherwise
 * the editor's built-in palette at the same offset.
 *
 * Examples (32x32):
 *  - empty frame: `"0:1024"`
 *  - two top rows of color 1: `"1:32 1:32 0:960"`
 */
export const CommitFrameInput = z.object({
  frameIndex: z.int().min(0),
  layerId: z.string().min(1),
  rle: z.string().min(1),
  paletteOverride: z.array(HexColorSchema).optional(),
}).register(domainRegistry, {
  id: "commit_frame_input",
  title: "Commit frame input",
  description:
    "Atomic write of a full frame on a single layer using palette-indexed RLE. One tool call replaces the whole layer grid for the target frame.",
  surface: "ai",
  llmExposed: true,
  group: "tools",
  examples: [{ frameIndex: 0, layerId: "L1", rle: "0:1024" }],
});
export type CommitFrameInput = z.infer<typeof CommitFrameInput>;

/**
 * Tool input: NES-style tilemap commit. The frame is partitioned into 8x8
 * tiles laid out row-major. `tiles[]` lists each unique tile as a 64-cell
 * RLE; `tilemap[]` indexes into `tiles[]` for every tile slot in the frame
 * (16 entries for a 32x32 canvas: 4 tile-columns x 4 tile-rows).
 *
 * Palette semantics match `CommitFrameInput`.
 */
export const CommitTilemapInput = z.object({
  frameIndex: z.int().min(0),
  layerId: z.string().min(1),
  tileSize: z.literal(8),
  tiles: z.array(z.string().min(1)),
  tilemap: z.array(z.int().min(0)),
  paletteOverride: z.array(HexColorSchema).optional(),
}).register(domainRegistry, {
  id: "commit_tilemap_input",
  title: "Commit tilemap input",
  description:
    "Atomic write of a full frame on a single layer by stitching 8x8 RLE tiles via a row-major tilemap. Lets the model reuse identical 8x8 regions across the frame.",
  surface: "ai",
  llmExposed: true,
  group: "tools",
  examples: [
    {
      frameIndex: 0,
      layerId: "L1",
      tileSize: 8,
      tiles: ["0:64"],
      tilemap: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    },
  ],
});
export type CommitTilemapInput = z.infer<typeof CommitTilemapInput>;

/**
 * Tool input: drop a full character archetype into the editor in one call.
 *
 * Lays out a state machine (states + transitions + initial), allocates a
 * sequential frame range per state (state[i] occupies frames
 * `[sum(frameCount[0..i-1]) .. sum(frameCount[0..i]) - 1]`), and resizes the
 * project's frame buffer to fit the total. Existing painted frames are
 * preserved up to the new total; any new frame slots are empty.
 *
 * Cross-validation: `initial` must appear in `states[].id`, and every
 * transition's `from` / `to` must too.
 */
export const ScaffoldAnimationSetInput = z
  .object({
    character: z.string().min(1).max(80),
    fps: z.int().min(1).max(60).optional(),
    initial: z.string().min(1),
    states: z
      .array(z.object({
        id: z.string().min(1),
        frameCount: z.int().min(1).max(64),
        loop: z.enum(["forward", "reverse", "pingpong", "once"]).optional(),
      }))
      .min(1),
    transitions: z
      .array(z.object({
        from: z.string().min(1),
        to: z.string().min(1),
        event: z.string().min(1),
      }))
      .optional(),
  })
  .superRefine((value, ctx) => {
    const stateIds = new Set(value.states.map((s) => s.id));
    const seen = new Set<string>();
    value.states.forEach((s, i) => {
      if (seen.has(s.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["states", i, "id"],
          message: `duplicate state id "${s.id}"`,
        });
      } else {
        seen.add(s.id);
      }
    });
    if (!stateIds.has(value.initial)) {
      ctx.addIssue({
        code: "custom",
        path: ["initial"],
        message: `initial "${value.initial}" must reference a state in states[]`,
      });
    }
    const transitions = value.transitions ?? [];
    transitions.forEach((t, i) => {
      if (!stateIds.has(t.from)) {
        ctx.addIssue({
          code: "custom",
          path: ["transitions", i, "from"],
          message: `transition.from "${t.from}" must reference a state in states[]`,
        });
      }
      if (!stateIds.has(t.to)) {
        ctx.addIssue({
          code: "custom",
          path: ["transitions", i, "to"],
          message: `transition.to "${t.to}" must reference a state in states[]`,
        });
      }
    });
  })
  .register(domainRegistry, {
    id: "scaffold_animation_set_input",
    title: "Scaffold animation set input",
    description:
      "Atomic character archetype: states, transitions, initial, and per-state frame allocation. Resets the state machine, allocates sequential frame ranges, and resizes the frame buffer in a single store transaction.",
    surface: "ai",
    llmExposed: true,
    group: "tools",
    examples: [
      {
        character: "frog",
        fps: 12,
        initial: "idle",
        states: [
          { id: "idle", frameCount: 2, loop: "forward" },
          { id: "hop", frameCount: 4, loop: "once" },
          { id: "land", frameCount: 2, loop: "once" },
        ],
        transitions: [
          { from: "idle", to: "hop", event: "JUMP" },
          { from: "hop", to: "land", event: "DONE" },
          { from: "land", to: "idle", event: "DONE" },
        ],
      },
    ],
  });
export type ScaffoldAnimationSetInput = z.infer<typeof ScaffoldAnimationSetInput>;

/** Tool output: total frame count after scaffold + per-state inclusive frame ranges. */
export const ScaffoldAnimationSetOutput = z
  .object({
    totalFrames: z.int(),
    ranges: z.record(z.string(), z.tuple([z.int(), z.int()])),
  })
  .register(domainRegistry, {
    id: "scaffold_animation_set_output",
    title: "Scaffold animation set output",
    description: "Echo of the layout the scaffold produced: total frame count and per-state inclusive [start, end].",
    surface: "ai",
    llmExposed: false,
    group: "tools",
  });
export type ScaffoldAnimationSetOutput = z.infer<typeof ScaffoldAnimationSetOutput>;

/**
 * Tool input: critique a rendered frame thumbnail against a stated intent.
 *
 * The agent calls this after `commit_frame` / `commit_tilemap` to close the
 * vision feedback loop. The model receives the PNG data URL alongside the
 * user's intent and a specific question, and may suggest follow-up tool
 * calls (e.g. another `commit_frame` to fix a problem the model spotted).
 */
export const RenderAndCritiqueInput = z
  .object({
    pngDataUrl: z.string().regex(/^data:image\/png;base64,/),
    intent: z.string().min(1).max(400),
    question: z.string().min(1).max(400),
  })
  .register(domainRegistry, {
    id: "render_and_critique_input",
    title: "Render-and-critique input",
    description:
      "Vision feedback for a freshly committed frame: PNG data URL + the user's intent + a specific aspect to critique.",
    surface: "ai",
    llmExposed: true,
    group: "tools",
    examples: [{
      pngDataUrl: "data:image/png;base64,iVBORw0KGgo...",
      intent: "frog walk-cycle frame 2 of 4",
      question: "does the leg pose read as mid-stride?",
    }],
  });
export type RenderAndCritiqueInput = z.infer<typeof RenderAndCritiqueInput>;

/**
 * Tool output: prose critique plus zero-or-more suggested follow-up tool
 * calls. The agent may execute the suggestions, ignore them, or refine.
 */
export const RenderAndCritiqueOutput = z
  .object({
    critique: z.string(),
    suggestedTools: z
      .array(z.object({
        name: z.string(),
        args: z.unknown(),
      }))
      .optional(),
  })
  .register(domainRegistry, {
    id: "render_and_critique_output",
    title: "Render-and-critique output",
    description: "Prose critique of the rendered frame plus optional follow-up tool suggestions.",
    surface: "ai",
    llmExposed: false,
    group: "tools",
  });
export type RenderAndCritiqueOutput = z.infer<typeof RenderAndCritiqueOutput>;

/**
 * Tool input: propose a palette of size N derived from a user-uploaded
 * reference image. Accepts PNG, JPEG, or WebP data URLs.
 */
export const ProposePaletteForImageInput = z
  .object({
    pngDataUrl: z.string().regex(/^data:image\/(png|jpe?g|webp);base64,/),
    size: z.int().min(2).max(64).optional(),
  })
  .register(domainRegistry, {
    id: "propose_palette_for_image_input",
    title: "Propose-palette-for-image input",
    description: "Reference image (data URL) and optional palette size for vision-driven palette extraction.",
    surface: "ai",
    llmExposed: true,
    group: "tools",
    examples: [{
      pngDataUrl: "data:image/png;base64,iVBORw0KGgo...",
      size: 16,
    }],
  });
export type ProposePaletteForImageInput = z.infer<typeof ProposePaletteForImageInput>;

/** Tool output: extracted palette plus an optional rationale string. */
export const ProposePaletteForImageOutput = z
  .object({
    palette: z.array(HexColorSchema),
    rationale: z.string().optional(),
  })
  .register(domainRegistry, {
    id: "propose_palette_for_image_output",
    title: "Propose-palette-for-image output",
    description: "Hex palette extracted from the reference image plus optional rationale.",
    surface: "ai",
    llmExposed: false,
    group: "tools",
  });
export type ProposePaletteForImageOutput = z.infer<typeof ProposePaletteForImageOutput>;

/**
 * Tool input: ask the agent to PROPOSE a state-machine archetype from a
 * natural-language intent. The proposal is surfaced to the user in the
 * MachineEditor as a Apply/Dismiss panel; the agent does NOT mutate the
 * project. Use this instead of `scaffold_animation_set` for any fresh
 * character / animation-set request — `scaffold_animation_set` writes
 * destructively without confirmation.
 *
 * `hints` is optional steering (fps, state count, frames-per-state). The
 * model is free to ignore hints when the intent contradicts them.
 */
export const ProposeStateMachineInput = z
  .object({
    intent: z.string().min(1).max(800),
    hints: z
      .object({
        fps: z.int().min(1).max(60).optional(),
        minStates: z.int().min(1).max(16).optional(),
        maxStates: z.int().min(1).max(16).optional(),
        framesPerStateHint: z.int().min(1).max(64).optional(),
      })
      .optional(),
  })
  .register(domainRegistry, {
    id: "propose_state_machine_input",
    title: "Propose-state-machine input",
    description:
      "Natural-language intent (and optional hints) for a proposed character archetype. Returns a structured ScaffoldAnimationSetInput-shaped proposal that the user must explicitly apply; the agent does NOT mutate the project.",
    surface: "ai",
    llmExposed: true,
    group: "tools",
    examples: [{ intent: "a frog that idles, jumps, and lands" }],
  });
export type ProposeStateMachineInput = z.infer<typeof ProposeStateMachineInput>;

/**
 * Tool output: a structured proposal (re-using ScaffoldAnimationSetInput's
 * exact shape) plus a one-paragraph rationale for the user to read in the
 * proposal panel. The browser surfaces this via the `proposal.set` store
 * event; the user clicks Apply to dispatch `proposal.apply`, which in turn
 * runs the same `machine.scaffold` reducer the unilateral tool would have.
 */
export const ProposeStateMachineOutput = z
  .object({
    proposal: ScaffoldAnimationSetInput,
    rationale: z.string().max(2000),
  })
  .register(domainRegistry, {
    id: "propose_state_machine_output",
    title: "Propose-state-machine output",
    description:
      "Structured ScaffoldAnimationSetInput-shaped proposal plus a short rationale string. Surfaced to the user; never auto-applied.",
    surface: "ai",
    llmExposed: false,
    group: "tools",
  });
export type ProposeStateMachineOutput = z.infer<typeof ProposeStateMachineOutput>;

/**
 * Tool input: overwrite the agent's persistent working notes (cross-turn
 * scratchpad). The agent owns the content; the field is free-form prose
 * intended for capturing intent, partial plans, or remaining tasks across
 * turns ("frames 0-2 done; frame 3 needs the leg in mid-stride").
 */
export const UpdateWorkingNotesInput = z
  .object({
    notes: z.string().max(2000),
  })
  .register(domainRegistry, {
    id: "update_working_notes_input",
    title: "Update working notes input",
    description:
      "Replace the agent's persistent working-notes scratchpad. Free-form prose, capped at 2000 characters; the agent owns the content.",
    surface: "ai",
    llmExposed: true,
    group: "tools",
    examples: [{ notes: "frog walk-cycle: frames 0-1 done; frame 2 needs mid-stride leg." }],
  });
export type UpdateWorkingNotesInput = z.infer<typeof UpdateWorkingNotesInput>;

/** Tool output: echo of the new notes plus the DO-side ISO timestamp. */
export const UpdateWorkingNotesOutput = z
  .object({
    notes: z.string(),
    updatedAt: z.string(),
  })
  .register(domainRegistry, {
    id: "update_working_notes_output",
    title: "Update working notes output",
    description: "Echo of the persisted working notes and the Durable Object's update timestamp (ISO).",
    surface: "ai",
    llmExposed: false,
    group: "tools",
  });
export type UpdateWorkingNotesOutput = z.infer<typeof UpdateWorkingNotesOutput>;
